/**
 * Popups, Social & Agent APIs
 */
import type { ItemResponse, ListResponse, DeleteResponse } from '../../types/api.js';
import type { Campaign } from '../../types/models.js';
import { apiRequest } from './core.js';

interface PopupSummary { id: string; name: string; status: string; type: string; design: Record<string, unknown>; content: Record<string, unknown>; triggers: Record<string, unknown>; targeting: Record<string, unknown> | null; createdAt: string; updatedAt: string; _count?: { submissions: number; analytics: number } }
interface PopupDetail extends PopupSummary { submissions: Array<{ id: string; email: string; phone?: string; name?: string; createdAt: string }>; analytics: Array<PopupAnalyticsEntry> }
interface PopupAnalyticsEntry { id: string; date: string; views: number; clicks: number; submissions: number; dismissals: number }
interface PopupAnalyticsResponse { analytics: Array<PopupAnalyticsEntry>; totals: { views: number; clicks: number; submissions: number; dismissals: number }; conversionRate: number; period: { days: number; from: string; to: string } }

export interface SocialAccount { id: string; platform: string; platformId: string; name: string; avatarUrl: string | null; expiresAt: string | null; createdAt: string; updatedAt: string }
/** Live counts from GET /social/accounts/:id/stats — null when the provider does not expose them. */
export interface SocialAccountStats {
  platform: string;
  followers: number | null;
  following: number | null;
  subscribers: number | null;
}
/** One live check from GET /social/accounts/health. `detail` is the platform's own words. */
export interface SocialAccountHealth { id: string; platform: string; name: string; expiresAt: string | null; hasRefreshToken: boolean; status: 'ok' | 'reconnect' | 'error'; detail: string; checkedAt: string }
/** Platform names as the API spells them (Prisma's SocialPlatform enum). */
export type SocialPlatformName =
  | 'FACEBOOK'
  | 'INSTAGRAM'
  | 'LINKEDIN'
  | 'TIKTOK'
  | 'PINTEREST'
  | 'YOUTUBE'
  | 'X';
/** The two states a post can be created or edited in; the rest are terminal. */
export type SocialPostDraftStatus = 'DRAFT' | 'SCHEDULED';
/** What the composer built. Mirrors the SocialPost.contentType column. */
export type SocialContentType = 'post' | 'reel' | 'story' | 'carousel' | 'dm' | 'shorts';

/**
 * What the composer sends. `accountIds` is optional — the server resolves the
 * workspace's connected accounts for the chosen platforms, so the composer
 * does not make the merchant re-pick an account they connected once.
 */
/**
 * How a YouTube upload should land. Omitted means `private` — the server's
 * default, and the only safe one: a video that goes public by mistake cannot
 * be un-published.
 */
export type YouTubePrivacy = 'private' | 'unlisted' | 'public';

/** YouTube composer fields other than privacy. Privacy stays on youtubePrivacy. */
export interface YouTubeSettingsPayload {
  hashtags?: string[];
  titleOverride?: string;
  description?: string;
  tags?: string[];
  categoryId?: string;
  license?: 'youtube' | 'creativeCommon';
  embeddable?: boolean;
  publicStatsViewable?: boolean;
  madeForKids?: boolean;
  containsSyntheticMedia?: boolean;
  paidProductPlacement?: boolean;
  defaultLanguage?: string;
  defaultAudioLanguage?: string;
  allowedCountries?: string[];
  blockedCountries?: string[];
  recordingDate?: string;
  thumbnailUrl?: string;
  notifySubscribers?: boolean;
  firstComment?: string;
}

/** Facebook New Post settings blob (location, link, first comment, share-to-story). */
export interface FacebookSettingsPayload {
  locationId?: string;
  locationName?: string;
  link?: string;
  firstComment?: string;
  shareToStory?: boolean;
}

/** Instagram New Post settings blob (location, collaborator, alt text, flags). */
export interface InstagramSettingsPayload {
  locationId?: string;
  locationName?: string;
  firstComment?: string;
  collaborator?: string;
  altText?: string;
  disableComments?: boolean;
  shareToStory?: boolean;
}

/** One hit from GET /social/meta/places. */
export interface MetaPlaceResult {
  id: string;
  name: string;
  city?: string;
  country?: string;
  hasLocation: boolean;
}

export interface CreateSocialPostInput {
  content?: string;
  mediaUrls?: string[];
  platforms: SocialPlatformName[];
  accountIds?: string[];
  scheduledAt: string;
  contentType?: SocialContentType;
  status?: SocialPostDraftStatus;
  youtubePrivacy?: YouTubePrivacy;
  youtubeSettings?: YouTubeSettingsPayload;
  facebookSettings?: FacebookSettingsPayload;
  instagramSettings?: InstagramSettingsPayload;
  /** X bills a post that carries a link; true lets this post carry one. */
  xAllowUrlCost?: boolean;
}

export interface UpdateSocialPostInput {
  content?: string;
  mediaUrls?: string[];
  platforms?: SocialPlatformName[];
  accountIds?: string[];
  scheduledAt?: string;
  contentType?: SocialContentType;
  status?: SocialPostDraftStatus;
  youtubePrivacy?: YouTubePrivacy;
  youtubeSettings?: YouTubeSettingsPayload;
  facebookSettings?: FacebookSettingsPayload;
  instagramSettings?: InstagramSettingsPayload;
  xAllowUrlCost?: boolean;
}

/** The post shape every write endpoint echoes back. */
export interface SocialPostSummary {
  id: string;
  platforms: SocialPlatformName[];
  content: string | null;
  mediaUrls: string[];
  scheduledAt: string;
  contentType: SocialContentType | null;
  status: string;
  accountIds: string[];
}

/** A comment / story-reply rule acting on one post (GET /social/posts/:id/triggers). */
export interface SocialPostTrigger {
  id: string;
  source: 'comment_rule' | 'instagram_rule';
  name: string;
  platform: string;
  trigger: 'comment' | 'story_reply';
  scope: 'this_post' | 'all_posts';
  keywords: string[];
  anyComment: boolean;
  publicReply: boolean;
  sendsDm: boolean;
  dmText: string | null;
  active: boolean;
}

/**
 * What a popup write accepts — ONE shape for create and update.
 *
 * They were two literals, and `update` omitted `startFlowOnTrigger` and
 * `flowId`. The API and the service have accepted both on PATCH throughout
 * (routes/popups.ts:108 destructures them; updatePopup turns flowId into a
 * connect/disconnect), so the pair could be set when a popup was created and
 * never afterwards — the compiler refused. Sharing the type is what stops the
 * two drifting apart again.
 *
 * `flowId: null` is meaningful and distinct from omitting it: undefined means
 * "leave the binding alone", null means "disconnect it". A merchant clearing
 * their choice needs the second.
 */
export interface PopupWriteInput {
  name?: string;
  type?: string;
  status?: string;
  design?: Record<string, unknown>;
  content?: Record<string, unknown>;
  triggers?: Record<string, unknown>;
  targeting?: Record<string, unknown>;
  startFlowOnTrigger?: boolean;
  flowId?: string | null;
}

export const popupsApi = {
  list: () => apiRequest<ListResponse<PopupSummary>>('/popups'),
  get: (id: string) => apiRequest<ItemResponse<PopupDetail>>(`/popups/${id}`),
  create: (data: PopupWriteInput & { name: string }) =>
    apiRequest<ItemResponse<{ id: string; name: string; status: string; type: string }>>('/popups', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: PopupWriteInput) =>
    apiRequest<ItemResponse<{ id: string; name: string; status: string }>>(`/popups/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) => apiRequest<DeleteResponse>(`/popups/${id}`, { method: 'DELETE' }),
  getAnalytics: (id: string, days?: number) => apiRequest<ItemResponse<PopupAnalyticsResponse>>(`/popups/${id}/analytics${days ? `?days=${days}` : ''}`)
};

export const socialApi = {
  getAccounts: () => apiRequest<ListResponse<SocialAccount>>('/social/accounts'),
  connectAccount: (data: { platform: string; platformId: string; name: string; avatarUrl?: string; accessToken: string; refreshToken?: string; expiresAt?: string }) =>
    apiRequest<ItemResponse<{ account: { id: string; platform: string; platformId: string; name: string; avatarUrl: string | null }; message: string }>>('/social/accounts/connect', { method: 'POST', body: JSON.stringify(data) }),
  disconnectAccount: (id: string) => apiRequest<DeleteResponse>(`/social/accounts/${id}`, { method: 'DELETE' }),
  /** Live-checks each connected account with the token a publish would use. */
  getAccountHealth: (limit = 100) => apiRequest<ListResponse<SocialAccountHealth>>(`/social/accounts/health?limit=${limit}`),
  /** Live followers/following/subscribers for View Profile (null = not available). */
  getAccountStats: (id: string) =>
    apiRequest<ItemResponse<SocialAccountStats>>(`/social/accounts/${encodeURIComponent(id)}/stats`),
  /** Calendar range. `month`/`year` is what the endpoint filters on. */
  getPosts: (params?: { month?: number; year?: number; platform?: string; status?: string }) => {
    const entries = Object.entries(params ?? {}).filter(([, v]) => v !== undefined);
    const queryString = entries.length > 0
      ? `?${new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString()}`
      : '';
    return apiRequest<ListResponse<SocialPostSummary>>(`/social/posts${queryString}`);
  },
  // The write endpoints return the post itself under `data`, not a { post }
  // wrapper — the old signatures claimed a wrapper that never existed.
  createPost: (data: CreateSocialPostInput) =>
    apiRequest<ItemResponse<SocialPostSummary>>('/social/posts', { method: 'POST', body: JSON.stringify(data) }),
  updatePost: (id: string, data: UpdateSocialPostInput) =>
    apiRequest<ItemResponse<SocialPostSummary>>(`/social/posts/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deletePost: (id: string) => apiRequest<DeleteResponse>(`/social/posts/${id}`, { method: 'DELETE' }),
  getPostTriggers: (id: string) => apiRequest<ListResponse<SocialPostTrigger>>(`/social/posts/${id}/triggers`),
  /** Pages Search for Facebook/Instagram location tagging. */
  searchMetaPlaces: (params: { q: string; mode?: 'facebook' | 'instagram'; limit?: number }) => {
    const qs = new URLSearchParams({ q: params.q });
    if (params.mode) qs.set('mode', params.mode);
    if (params.limit !== undefined) qs.set('limit', String(Math.min(params.limit, 100)));
    return apiRequest<ListResponse<MetaPlaceResult>>(`/social/meta/places?${qs.toString()}`);
  },
  publishNow: (id: string) => {
    // Server waits up to 180s for the worker; abort slightly after so the UI
    // cannot stay on "Publishing…" forever if the HTTP wait never settles.
    const PUBLISH_NOW_CLIENT_TIMEOUT_MS = 195_000;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), PUBLISH_NOW_CLIENT_TIMEOUT_MS);
    return apiRequest<ItemResponse<{
      success: boolean;
      jobId?: string;
      publishedAt?: string;
      warning?: string;
    }>>(`/social/posts/${id}/publish-now`, {
      method: 'POST',
      signal: controller.signal,
    }).finally(() => {
      window.clearTimeout(timer);
    });
  },
};

/** Agent analytics response shape */
export interface AgentAnalyticsData {
  metrics: {
    resolutionRate: number;
    resolutionRateChange: number;
    avgResponseTime: number;
    avgResponseTimeChange: number;
    csatScore: number;
    csatScoreChange: number;
    moneySaved: number;
    moneySavedChange: number;
  };
  stats: {
    automationRate: number;
    escalationsToday: number;
    customersHelped: number;
    intentAccuracy: number;
  };
  conversationVolume: { date: string; total: number; aiHandled: number; humanHandled: number }[];
  resolutionRateTrend: { date: string; rate: number }[];
  topIntents: { intent: string; count: number; percentage: number }[];
  sentimentDistribution: { name: string; value: number; color: string }[];
  responseTimeByHour: { hour: string; time: number }[];
  totalConversations: number;
}

export const marketingApi = {
  getFunnel: (workspaceId: string) => apiRequest<ItemResponse<{ stages: Record<string, number> }>>(`/marketing/funnel?workspaceId=${workspaceId}`),
  getCampaigns: (workspaceId: string) => apiRequest<ListResponse<Campaign>>(`/marketing/campaigns?workspaceId=${workspaceId}`),
  syncMeta: () => apiRequest<ItemResponse<{ message: string; campaigns: Campaign[] }>>('/marketing/sync/meta', { method: 'POST' }),
  seedData: (workspaceId: string) => apiRequest<ItemResponse<{ message: string }>>('/marketing/seed', { method: 'POST', body: JSON.stringify({ workspaceId }) })
};

export const vendorApi = {
  generateAccess: (supplierId: string) => apiRequest<ItemResponse<{ accessKey: string; portalUrl: string }>>(`/vendor/generate-access/${supplierId}`, { method: 'POST' }),
  revokeAccess: (supplierId: string) => apiRequest<ItemResponse<{ message: string }>>(`/vendor/revoke-access/${supplierId}`, { method: 'POST' })
};
